import React from 'react';
import { Snackbar, IconButton, Box, Typography, Slide } from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { Alert, AlertTitle } from '@material-ui/lab';

// Definimos transición opcional (desde la izquierda)
function TransitionUp(props) {
  return <Slide {...props} direction="left" />;
}

const useStyles = makeStyles((theme) => ({
  snackbar: {
    zIndex: theme.zIndex.snackbar + 1000, // Asegurar que aparezca por encima de otros elementos
    '& .MuiSnackbarContent-root': {
      minWidth: '400px',
      maxWidth: '600px',
    }
  },
  alert: {
    '& .MuiAlert-message': {
      width: '100%',
    }
  },
  alertContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1),
  },
  messageContainer: {
    flex: 1,
  },
  curtainInfo: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.875rem',
    opacity: 0.8,
  },
  actionBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginLeft: theme.spacing(1),
  },
  openBadge: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
  closeBadge: {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.secondary.contrastText,
  },
}));

/**
 * Componente para mostrar notificaciones de operaciones de cortinas
 */
const CurtainNotification = ({ 
  notification, 
  open, 
  onClose, 
  autoHideDuration = 35000 
}) => {
  const classes = useStyles();

  if (!notification) {
    return null;
  }

  const { type, title, message, curtainName, action, timestamp } = notification;

  // Determinar el tipo de alerta basado en el tipo de notificación
  const getAlertSeverity = (type) => {
    switch (type) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
      default:
        return 'info';
    }
  };

  // Obtener el color del badge según la acción
  const getBadgeClass = (action) => {
    switch (action) {
      case 'open':
      case 0:
        return classes.openBadge;
      case 'close':
      case 1:
        return classes.closeBadge;
      default:
        return classes.openBadge;
    }
  };

  // Obtener el texto de la acción
  const getActionText = (action) => {
    if (action === 'open' || action === 0) return 'Abrir';
    if (action === 'close' || action === 1) return 'Cerrar';
    return 'Operación';
  };

  // Formatear el tiempo transcurrido
  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - notificationTime) / 1000);

    if (diffInSeconds < 60) {
      return 'Ahora';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `Hace ${minutes} min`;
    } else {
      const hours = Math.floor(diffInSeconds / 3600);
      return `Hace ${hours} h`;
    }
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={7000}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      TransitionComponent={TransitionUp}
      className={classes.snackbar}
    >
      <Alert
        severity={getAlertSeverity(type)}
        className={classes.alert}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        <AlertTitle>
          <Box display="flex" alignItems="center">
            {title}
            {action !== undefined && (
              <span className={`${classes.actionBadge} ${getBadgeClass(action)}`}>
                {getActionText(action)}
              </span>
            )}
          </Box>
        </AlertTitle>
        
        <div className={classes.alertContent}>
          <div className={classes.messageContainer}>
            <Typography variant="body2">
              {message}
            </Typography>
            
            {curtainName && (
              <div className={classes.curtainInfo}>
                <strong>Cortina:</strong> {curtainName}
                {timestamp && (
                  <span style={{ float: 'right' }}>
                    {getTimeAgo(timestamp)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </Alert>
    </Snackbar>
  );
};

export default CurtainNotification;

